/* eslint-disable react/jsx-handler-names */
import { T } from '@/components/i18n/message.i18n.component';
import { Loader } from '@/components/ui/loader/loader.component';
import { Pill } from '@/components/ui/pill/pill.component';
import { Switch } from '@/components/ui/switch/switch.component';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs/tabs.component';
import { DesignObligation, MaturityStage, ProductBuildingBlock } from '@/gql/graphql';
import {
  GroupedBuildingBlock,
  SortByEnum,
  SortedBuildingBlock,
} from '@/hooks/use-building-blocks/use-building-blocks.hook.core';
import { pretailoringPageMessages } from '@/pages/pre-tailoring/pre-tailoring.messages';
import { useIntl } from 'react-intl';
import { BuildingBlockList } from './building-blocks-list.component/building-blocks-list.component';

type Props = {
  loading?: boolean;
  openModalOnClick?: boolean;
  disableOpenDialog?: boolean;
  selectedBuildingBlockId?: string;
  allowBuildingBlockSelection?: boolean;
  isProductActive?: boolean;
  includedBuildingBlocks: SortedBuildingBlock<DesignObligation | MaturityStage>;
  excludedBuildingBlocks: SortedBuildingBlock<DesignObligation | MaturityStage>;
  sort: 'DO' | 'MTS';
  handleSortChange: (sort: SortByEnum) => void;
  onTabChange?: (context: 'included' | 'excluded') => void;
  onBlockClick?: (bb: ProductBuildingBlock) => void;
  setSelectedBB?: (bb: ProductBuildingBlock | undefined) => void;
  onBBInclusionChange?: (groupedBB: GroupedBuildingBlock<DesignObligation>, including: boolean) => void;
};

export const BuildingBlocks = ({
  loading,
  sort,
  handleSortChange,
  isProductActive,
  selectedBuildingBlockId,
  onTabChange,
  onBBInclusionChange,
  onBlockClick: handleBlockClick,
  includedBuildingBlocks,
  excludedBuildingBlocks,
}: Props) => {
  const t = useIntl();
  return (
    <div className="flex flex-col gap-4">
      <Tabs className="flex flex-col gap-y-4" defaultValue="included">
        <div className="flex flex-row justify-between items-center">
          <div data-testid="building-blocks-sort-by" className="flex flex-row items-center gap-x-4">
            <p className="typo-primary-400" data-testid="building-blocks-sort-by-text">
              <T {...pretailoringPageMessages.lifeCycleSortBy} />
            </p>
            <div className="flex flex-row items-center gap-x-2">
              <Pill variant="do" text="Design Obligation" />
              <Switch
                data-testid="building-blocks-sort-by-trigger"
                checked={sort === 'MTS' ? true : false}
                onCheckedChange={(value: boolean) => {
                  if (value) handleSortChange(SortByEnum.MTS);
                  else handleSortChange(SortByEnum.DO);
                }}
              />
              <Pill variant="ms" text="Maturity Stage" />
            </div>
          </div>
          {loading && <Loader className="w-4 h-4" />}
        </div>

        <div>
          <TabsList className="mb-5" data-testid="building-blocks-tabs-list">
            <TabsTrigger
              value="included"
              data-testid="building-blocks-included-tab-trigger"
              onClick={() => {
                onTabChange && onTabChange('included');
              }}
            >
              <p className="typo-primary-500">
                {t.formatMessage(pretailoringPageMessages.lifecycleIncludedTitle, {
                  count: includedBuildingBlocks.count,
                })}
              </p>
            </TabsTrigger>
            <TabsTrigger
              value="excluded"
              data-testid="building-blocks-excluded-tab-trigger"
              onClick={() => {
                onTabChange && onTabChange('excluded');
              }}
            >
              <p className="typo-primary-500">
                {t.formatMessage(pretailoringPageMessages.lifecycleExcludedTitle, {
                  count: excludedBuildingBlocks.count,
                })}
              </p>
            </TabsTrigger>
          </TabsList>
          <TabsContent
            value="included"
            className="flex flex-col gap-y-4 data-[state=inactive]:hidden"
            data-testid="building-blocks-included-tab-content"
          >
            <BuildingBlockList
              sort={sort}
              context="included"
              buildingBlocks={includedBuildingBlocks}
              isProductActive={isProductActive}
              selectedBuildingBlockId={selectedBuildingBlockId}
              handleBlockClick={handleBlockClick}
              onBBInclusionChange={onBBInclusionChange}
            />
          </TabsContent>
          <TabsContent
            value="excluded"
            className="flex flex-col gap-y-4 data-[state=inactive]:hidden"
            data-testid="building-blocks-excluded-tab-content"
          >
            <BuildingBlockList
              sort={sort}
              context="excluded"
              buildingBlocks={excludedBuildingBlocks}
              isProductActive={isProductActive}
              selectedBuildingBlockId={selectedBuildingBlockId}
              handleBlockClick={handleBlockClick}
              onBBInclusionChange={onBBInclusionChange}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};
